import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Key, TrendingUp, Shield, Users, Wrench, MessageSquare, CheckCircle2, Phone, ChevronRight, Star, Award } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import PropertyCard from "../components/PropertyCard";
import TestimonialCard from "../components/TestimonialCard";
import NewsCard from "../components/NewsCard";
import TrustBadges from "../components/TrustBadges";
import ZooplaBranchButton from "../components/ZooplaBranchButton";
import ZooplaStrip from "../components/ZooplaStrip";
import { HomeSEO } from "../components/SEO";
import api from "../lib/api";
import logger from "../lib/logger";

const WHY_CHOOSE_DEFAULTS = {
  title: "Why Landlords Choose Keystone Properties",
  image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800",
  experience_years: "6+",
};

const HomePage = () => {
  const [properties, setProperties] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [googleReviews, setGoogleReviews] = useState({ reviews: [], rating: 5, total_reviews: 0 });
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whyChooseContent, setWhyChooseContent] = useState(WHY_CHOOSE_DEFAULTS);
  const [zooplaStripContent, setZooplaStripContent] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Use allSettled so a single failed endpoint doesn't blank out the rest
        const results = await Promise.allSettled([
          api.getProperties({ available_only: true }),
          api.getTestimonials(),
          api.getNews(null, 3),
          api.getSelectedReviews(),
          api.getContentSection("homepage_why_choose"),
          api.getContentSection("homepage_zoopla"),
        ]);
        const [propertiesRes, testimonialsRes, newsRes, reviewsRes, whyChooseRes, zooplaRes] = results;
        if (whyChooseRes.status === "fulfilled" && whyChooseRes.value?.content) {
          setWhyChooseContent({ ...WHY_CHOOSE_DEFAULTS, ...whyChooseRes.value.content });
        }
        if (zooplaRes.status === "fulfilled" && zooplaRes.value?.content) {
          setZooplaStripContent(zooplaRes.value.content);
        }
        if (propertiesRes.status === "fulfilled") setProperties(propertiesRes.value.slice(0, 6));
        if (testimonialsRes.status === "fulfilled") setTestimonials(testimonialsRes.value);
        if (newsRes.status === "fulfilled") setNews(newsRes.value);
        if (reviewsRes.status === "fulfilled") {
          setGoogleReviews(reviewsRes.value);
        } else {
          // Selected reviews failed — fall back to raw Google reviews
          try {
            const googleData = await api.getGoogleReviews();
            setGoogleReviews(googleData);
          } catch (e) {
            logger.error("Error fetching Google reviews fallback:", e);
          }
        }
        results.forEach((r, i) => {
          if (r.status === "rejected") {
            logger.error(`HomePage fetch slot ${i} failed:`, r.reason);
          }
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const services = [
    {
      icon: Building2,
      title: "Property Management",
      description: "Full hands-on management including tenant find, maintenance coordination, and rent collection.",
      link: "/services/property-management",
    },
    {
      icon: Award,
      title: "Management Packages",
      description: "Flexible Bronze, Silver, Gold and Platinum packages tailored for landlords and portfolio investors.",
      link: "/services/packages",
    },
    {
      icon: Key,
      title: "Lettings",
      description: "Professional marketing, thorough referencing, and swift tenancy setup for landlords.",
      link: "/services/lettings",
    },
    {
      icon: TrendingUp,
      title: "Investments",
      description: "Expert guidance on property investment opportunities across West Yorkshire.",
      link: "/services/investments",
    },
  ];

  const trustPoints = [
    { icon: Shield, text: "Local West Yorkshire team" },
    { icon: CheckCircle2, text: "Competitive management fees" },
    { icon: Wrench, text: "Fully managed lettings & maintenance" },
    { icon: MessageSquare, text: "Clear communication, no fuss" },
  ];

  const whyChooseUs = [
    {
      title: "Simple, hands-on management",
      description: "We handle tenant find, compliance, maintenance, rent collection and inspections—so you don't have to.",
    },
    {
      title: "Local expertise, better decisions",
      description: "We know West Yorkshire rental demand and pricing, helping reduce voids and attract the right tenants.",
    },
    {
      title: "Protecting your property",
      description: "Regular inspections, responsive maintenance coordination and clear reporting to keep standards high.",
    },
    {
      title: "Straightforward communication",
      description: "Quick updates, transparent processes and a dedicated point of contact.",
    },
  ];

  return (
    <div data-testid="home-page">
      <HomeSEO />
      {/* Hero Section */}
      <section className="relative bg-navy text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1658900355439-8dbec84f6f57?w=1920"
            alt="Modern UK Property"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/80" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-3xl">
            <span className="text-gold font-heading text-xl md:text-2xl font-semibold mb-4 block animate-fade-in">
              Keystone Properties
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mb-6 animate-fade-in">
              Professional Property Management in West Yorkshire
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed animate-fade-in animate-delay-100">
              Based in Batley, we help landlords in Leeds, Bradford, Batley, Dewsbury and surrounding areas 
              maximise rental income and protect their investment—without the day-to-day stress.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in animate-delay-200">
              <Button
                asChild
                size="lg"
                className="bg-gold hover:bg-gold-light text-navy font-semibold btn-primary"
                data-testid="hero-cta-valuation"
              >
                <Link to="/contact">
                  Book a Free Rental Valuation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-navy"
                data-testid="hero-cta-services"
              >
                <Link to="/services/property-management">
                  Explore Our Management Service
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Zoopla Rated Strip (CMS-driven, hidden until enabled in admin) */}
      <ZooplaStrip content={zooplaStripContent} />

      {/* Trust Bar */}
      <section className="bg-off-white py-8 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {trustPoints.map((point, index) => (
              <div key={point.text || index} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0">
                  <point.icon className="w-5 h-5 text-navy" />
                </div>
                <span className="text-navy font-medium text-sm">{point.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
              Our Services
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              Property Management | Lettings | Investments
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Comprehensive property services designed to maximise your investment 
              and minimise your workload.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <Card
                key={service.title || index}
                className="service-card border-slate-200 hover:shadow-lg transition-all duration-300"
              >
                <CardContent className="p-6 lg:p-8">
                  <div className="w-14 h-14 rounded-lg bg-navy/5 flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-navy" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-navy mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 mb-6">
                    {service.description}
                  </p>
                  <Link
                    to={service.link}
                    className="inline-flex items-center text-gold font-medium hover:text-gold-light transition-colors"
                  >
                    Learn more
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 md:py-28 bg-off-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
                Why Keystone
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-8">
                {whyChooseContent.title}
              </h2>
              <div className="space-y-6">
                {whyChooseUs.map((item, index) => (
                  <div key={item.title || index} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <CheckCircle2 className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-navy mb-2">
                        {item.title}
                      </h3>
                      <p className="text-slate-600">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src={whyChooseContent.image}
                alt="Property Management in West Yorkshire"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-navy text-white p-6 rounded-lg shadow-xl hidden lg:block">
                <div className="text-3xl font-heading font-bold text-gold mb-1">{whyChooseContent.experience_years}</div>
                <div className="text-sm text-white/80">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <TrustBadges variant="homepage" />

      {/* Featured Properties Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
            <div>
              <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
                Available Properties
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy">
                Featured Properties
              </h2>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                asChild
                variant="outline"
                className="border-navy text-navy hover:bg-navy hover:text-white"
                data-testid="view-all-properties"
              >
                <Link to="/properties">
                  View All Properties
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <ZooplaBranchButton
                variant="solid"
                label="See all on Zoopla"
                testId="zoopla-homepage-featured"
              />
            </div>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-96 skeleton rounded-lg" />
              ))}
            </div>
          ) : properties.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {properties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-500">No properties available at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* News Highlights */}
      {news.length > 0 && (
        <section className="py-20 md:py-28 bg-off-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
              <div>
                <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
                  News & Insights
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy">
                  Property News & Tips
                </h2>
              </div>
              <Button
                asChild
                variant="outline"
                className="border-navy text-navy hover:bg-navy hover:text-white"
              >
                <Link to="/news">
                  All Articles
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {news.map((article) => (
                <NewsCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Google Reviews Section */}
      {(googleReviews.reviews.length > 0 || testimonials.length > 0) && (
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
                Reviews
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
                What Our Clients Say
              </h2>
              {googleReviews.reviews.length > 0 && (
                <div className="flex flex-col items-center gap-2 mt-4">
                  <div className="flex items-center gap-2">
                    <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
                    <span className="text-slate-600 font-medium">Google Reviews</span>
                    <div className="flex text-gold ml-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-5 h-5 ${i < Math.round(googleReviews.rating) ? 'fill-current' : ''}`} />
                      ))}
                    </div>
                    <span className="text-slate-600 font-semibold ml-1">{googleReviews.rating}</span>
                  </div>
                  <p className="text-slate-500 text-sm">Based on {googleReviews.total_reviews} reviews</p>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {googleReviews.reviews.slice(0, 6).map((review) => (
                <Card key={review.id} className="border-slate-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      {review.profile_photo && (
                        <img 
                          src={review.profile_photo} 
                          alt={review.reviewer_name}
                          className="w-10 h-10 rounded-full"
                        />
                      )}
                      <div>
                        <p className="font-semibold text-navy">{review.reviewer_name}</p>
                        <p className="text-xs text-slate-500">{review.review_date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-4 h-4 ${i < review.rating ? 'text-gold fill-current' : 'text-slate-300'}`} 
                        />
                      ))}
                    </div>
                    <p className="text-slate-600 text-sm line-clamp-4">"{review.review_text}"</p>
                    <div className="flex items-center justify-end mt-4">
                      <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              ))}
              {googleReviews.reviews.length === 0 && testimonials.slice(0, 4).map((testimonial) => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjIiLz48L2c+PC9zdmc+')] opacity-20" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-heading text-3xl md:text-5xl font-semibold mb-6">
            Ready to hand over the hassle?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation rental valuation and management options tailored to your property.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-gold hover:bg-gold-light text-navy font-semibold btn-primary"
              data-testid="cta-valuation"
            >
              <Link to="/contact">
                Book a Free Rental Valuation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
          <div className="mt-8 flex items-center justify-center gap-2 text-white/70">
            <Phone className="w-5 h-5" />
            <span>Or call </span>
            <a href="tel:01924 520163" className="text-gold hover:text-gold-light font-medium">
              01924 520163
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
